/**
 * modals/DeleteEntryModelConfirmationModal.tsx
 * Confirmation modal for deleting entry models
 *
 * Shows smart warnings based on usage:
 * - If entry model is selected in current playbook: offers "Unselect" option
 * - If entry model is used in other playbooks: shows warning with playbook list
 */

// 1. External dependencies
import React from 'react';
import { AlertTriangle } from 'lucide-react';

// 2. Internal absolute imports
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button
} from '@enfomentis/ui';

// 3. Relative imports
import type { EntryModelDraft, PlaybookDraft } from '../hooks/usePlaybookBuilder';

/**
 * Props for DeleteEntryModelConfirmationModal
 */
interface DeleteEntryModelConfirmationModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** The entry model being deleted */
  entryModel: EntryModelDraft;
  /** ID of the current playbook */
  currentPlaybookId: string;
  /** All playbooks */
  playbooks: PlaybookDraft[];
  /** Whether item is currently selected in current playbook (including pending changes) */
  isSelectedInCurrentPlaybook: boolean;
  /** Handler for closing the modal */
  onClose: () => void;
  /** Handler for unselecting from current playbook */
  onUnselect: () => void;
  /** Handler for deleting the entry model */
  onDelete: () => void;
}

/**
 * Delete entry model confirmation modal component
 */
export const DeleteEntryModelConfirmationModal: React.FC<DeleteEntryModelConfirmationModalProps> = ({
  isOpen,
  entryModel,
  currentPlaybookId,
  playbooks,
  isSelectedInCurrentPlaybook,
  onClose,
  onUnselect,
  onDelete
}) => {
  // Find other playbooks using this entry model
  const otherPlaybooksUsing = playbooks.filter(
    p => p.tempId !== currentPlaybookId && p.entryModelIds?.includes(entryModel.tempId)
  );

  const hasOtherUsages = otherPlaybooksUsing.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onClose}
      size="md"
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="flex gap-2 items-center">
              {hasOtherUsages && <AlertTriangle className="w-5 h-5 text-warning" />}
              <span>Delete '{entryModel.name}'?</span>
            </ModalHeader>

            <ModalBody className="space-y-3">
              {isSelectedInCurrentPlaybook && !hasOtherUsages && (
                <p className="text-sm text-text-secondary">
                  This entry model is currently selected in this playbook.
                  <br />
                  <br />
                  Did you mean to unselect this instead?
                </p>
              )}

              {isSelectedInCurrentPlaybook && hasOtherUsages && (
                <>
                  <p className="text-sm text-text-secondary">
                    This entry model is selected in this playbook <strong>AND {otherPlaybooksUsing.length} other playbook(s)</strong>:
                  </p>
                  <ul className="list-disc list-inside text-sm text-text-secondary pl-2">
                    {otherPlaybooksUsing.map(playbook => (
                      <li key={playbook.tempId}>{playbook.name}</li>
                    ))}
                  </ul>
                  <p className="text-sm font-medium text-warning">
                    Deleting will remove it from ALL playbooks permanently.
                  </p>
                  <p className="text-sm text-text-secondary mt-2">
                    Did you mean to unselect from this playbook instead?
                  </p>
                </>
              )}

              {!isSelectedInCurrentPlaybook && hasOtherUsages && (
                <>
                  <p className="text-sm text-text-secondary">
                    This entry model is used in <strong>{otherPlaybooksUsing.length} playbook(s)</strong>:
                  </p>
                  <ul className="list-disc list-inside text-sm text-text-secondary pl-2">
                    {otherPlaybooksUsing.map(playbook => (
                      <li key={playbook.tempId}>{playbook.name}</li>
                    ))}
                  </ul>
                  <p className="text-sm font-medium text-warning">
                    Deleting will remove it from ALL playbooks permanently.
                  </p>
                </>
              )}

              {!isSelectedInCurrentPlaybook && !hasOtherUsages && (
                <p className="text-sm text-text-secondary">
                  This entry model is not currently selected in any playbook.
                  <br />
                  <br />
                  This action cannot be undone.
                </p>
              )}
            </ModalBody>

            <ModalFooter>
              <Button
                variant="light"
                onPress={onClose}
              >
                Cancel
              </Button>
              {isSelectedInCurrentPlaybook && (
                <Button
                  color="primary"
                  variant="flat"
                  onPress={() => {
                    onUnselect();
                    onClose();
                  }}
                >
                  {hasOtherUsages ? 'Unselect from This Playbook' : 'Unselect'}
                </Button>
              )}
              <Button
                color="danger"
                onPress={() => {
                  onDelete();
                  onClose();
                }}
              >
                {hasOtherUsages ? 'Delete from All Playbooks' : 'Delete'}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};
